/**
 * Où sont mes camions — carte et liste en direct, fonds de carte et filtres mémorisés.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import FleetMap, { MAP_LAYERS } from '../components/FleetMap';
import FleetList from '../components/FleetList';
import ZonesList from '../components/ZonesList';
import { SearchableSelect } from '../components/ui/searchable-select';
import { compterFlotte, estArretLong, estPositionFraiche } from '../components/map/fleetRules';
import useMaintenant from '../hooks/useMaintenant';
import { organizationOption } from '../lib/selectOptions';
import { api, geoLocationsAPI, organizationsAPI, trackingAPI, usersAPI } from '../lib/api';
import { Layers, List, RefreshCw, Truck as TruckIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { formatHeure } from '../lib/formats';
import { libelle, texteSur } from '../lib/libelles';
import { LEXIQUE } from '../lib/lexique';
import useFleetStream, { amorcerTraines, appendTrails, mergeFleetPoints } from '../hooks/useFleetStream';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import { MapCanvas } from '../components/ui/page-layouts';

const PREF_KEY = 'fleet_map_prefs_v1';
const ALERTES_POLL_MS = 60000;
const SNAPSHOT_POLL_MS = 15000;
// Flux direct actif : les positions arrivent par lui, le relevé ne sert plus
// qu'aux nouveaux camions et aux changements d'étape.
const SNAPSHOT_POLL_CONNECTE_MS = 60000;

const DEFAULT_PREFS = {
  layer: MAP_LAYERS.satellite ? 'satellite' : 'streets',
  showTrails: true,
  onlineOnly: false,
};

function loadLocalPrefs() {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {}
  return DEFAULT_PREFS;
}

function saveLocalPrefs(p) {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(p));
  } catch {}
}

export default function TrackingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('authToken');
  const [prefs, setPrefs] = useState(loadLocalPrefs);
  // Une préférence enregistrée « Satellite » retombe sur le plan tant que la clé Esri manque.
  const fondActif = MAP_LAYERS[prefs.layer] ? prefs.layer : 'streets';
  const [vehicles, setVehicles] = useState([]);
  const [history, setHistory] = useState({});
  // « Voir sur la carte » depuis la fiche camion : /tracking?camion=<id>.
  const [searchParams] = useSearchParams();
  const [selectedKey, setSelectedKey] = useState(() => searchParams.get('camion') || null);
  const [panneau, setPanneau] = useState('camions');
  // Seul le propriétaire de la plateforme voit plusieurs sociétés.
  const estSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const maintenant = useMaintenant();
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [geoLocations, setGeoLocations] = useState([]);
  const [trackingAlerts, setTrackingAlerts] = useState([]);
  const [alertesOuvertes, setAlertesOuvertes] = useState([]);
  const [showList, setShowList] = useState(true);

  // Load server preferences once and merge
  useEffect(() => {
    (async () => {
      try {
        const serverPrefs = await usersAPI.getMyPreferences();
        if (serverPrefs?.fleet_map) {
          setPrefs((p) => ({ ...p, ...serverPrefs.fleet_map }));
        }
      } catch {}
    })();
  }, []);

  // Load geo locations and alerts once on mount
  useEffect(() => {
    geoLocationsAPI.getAll({ status: 'active', limit: 1000 })
      .then((data) => setGeoLocations(data?.data || data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alertes ouvertes : bandeau de l'en-tête et marqueurs de la carte, relues
  // chaque minute (un arrêt critique ou un écart d'itinéraire s'ouvre en route).
  useEffect(() => {
    const charger = () =>
      api
        .get('/alerts/events?status=open')
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : [];
          setAlertesOuvertes(rows);
          setTrackingAlerts(
            rows.filter((event) =>
              /gps|tracking|geo|route_deviation|critical_stop/i.test(
                `${event.event_type || ''} ${event.title || ''} ${event.message || ''}`,
              ),
            ),
          );
        })
        .catch(() => {});
    charger();
    const minuterie = window.setInterval(charger, ALERTES_POLL_MS);
    return () => window.clearInterval(minuterie);
  }, []);

  useEffect(() => {
    if (!estSuperAdmin) return;
    organizationsAPI
      .getEvery()
      .then((resp) => setOrganizations(resp?.data || resp || []))
      .catch(() => {});
  }, [estSuperAdmin]);

  const organizationOptions = useMemo(
    () => [
      { value: 'all', label: 'Toutes les sociétés' },
      ...organizations.map(organizationOption),
    ],
    [organizations],
  );

  const persistPrefs = useCallback((next) => {
    setPrefs(next);
    saveLocalPrefs(next);
    // Persist to server (fire and forget)
    usersAPI.updateMyPreferences({ fleet_map: next }).catch(() => {});
  }, []);

  // La traîne des deux dernières heures n'est demandée qu'au premier relevé :
  // ensuite, le flux direct la prolonge point par point.
  const traineChargee = React.useRef(false);
  const refresh = useCallback(async () => {
    try {
      const params = estSuperAdmin && selectedOrg !== 'all' ? { organization_id: selectedOrg } : {};
      if (!traineChargee.current) params.include_tracks = true;
      const data = await trackingAPI.getFleetLive(params);
      const rows = Array.isArray(data) ? data : data?.data || [];
      if (params.include_tracks) {
        traineChargee.current = true;
        setHistory((prev) => amorcerTraines(prev, rows));
      }
      setVehicles(rows);
      setLastRefresh(new Date());
    } catch (e) {
      console.warn('Fleet refresh failed', e);
    } finally {
      setLoading(false);
    }
  }, [estSuperAdmin, selectedOrg]);

  // A fleet message used to be applied with one `setState` per point inside a
  // `forEach`: fifty trucks meant a hundred renders and a visibly stuttering
  // map. The batch is now folded in one pass.
  const applyFleetUpdate = useCallback((points) => {
    setHistory((prev) => appendTrails(prev, points));
    setVehicles((prev) => mergeFleetPoints(prev, points));
  }, []);

  const { connected } = useFleetStream({
    token,
    onFleetUpdate: applyFleetUpdate,
    onPoll: refresh,
    pollMs: SNAPSHOT_POLL_MS,
    pollMsConnecte: SNAPSHOT_POLL_CONNECTE_MS,
  });

  // Le flux ne filtre pas par société : on écarte ici les camions des autres.
  const deLaSociete = useMemo(
    () =>
      estSuperAdmin && selectedOrg !== 'all'
        ? vehicles.filter((v) => v.organization_id === selectedOrg)
        : vehicles,
    [vehicles, estSuperAdmin, selectedOrg],
  );

  const alertesDeLaSociete = useMemo(
    () =>
      estSuperAdmin && selectedOrg !== 'all'
        ? alertesOuvertes.filter((a) => a.organization_id === selectedOrg)
        : alertesOuvertes,
    [alertesOuvertes, estSuperAdmin, selectedOrg],
  );

  // « Arrêtés depuis plus de 2 h » : s'applique à la carte et à la liste.
  const [arretsLongsSeuls, setArretsLongsSeuls] = useState(false);
  const affiches = useMemo(
    () => (arretsLongsSeuls ? deLaSociete.filter((v) => estArretLong(v, maintenant)) : deLaSociete),
    [deLaSociete, arretsLongsSeuls, maintenant],
  );

  const filtered = useMemo(
    () =>
      prefs.onlineOnly
        ? affiches.filter((v) => estPositionFraiche(v, maintenant))
        : affiches,
    [affiches, prefs.onlineOnly, maintenant],
  );

  /* Les quatre états d'un camion suivi, comptés une fois pour toutes
   * (`compterFlotte`, règle partagée avec la carte et la liste).
   *
   * « Signal actif » dit si la position arrive; « en mouvement » et « à l'arrêt »
   * disent ce que le camion fait. Ce sont deux questions différentes, et un
   * camion hors ligne n'est ni à l'arrêt ni en mouvement — on ne sait pas.
   * On ne compte donc l'arrêt et le mouvement que parmi ceux qui répondent.
   */
  const { enLigne, horsLigne, enMouvement, aLArret, arretsLongs } = useMemo(() => {
    const c = compterFlotte(deLaSociete, maintenant);
    return {
      enLigne: c.enLigne,
      horsLigne: c.horsLigne,
      enMouvement: c.enMouvement,
      aLArret: c.aLArret,
      arretsLongs: c.arretsLongs,
    };
  }, [deLaSociete, maintenant]);
  const total = deLaSociete.length;
  const part = (n) => (total ? `${Math.round((n / total) * 100)} %` : '0 %');

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Terrain"
        title={LEXIQUE.menuSuivi}
        description="La position de vos camions en ce moment, et ceux qui ne donnent plus de nouvelles."
        stats={[
          {
            label: 'Signal actif',
            valeur: enLigne,
            ton: 'positif',
            unite: part(enLigne),
            aide: `${enLigne} camion(s) sur ${total} suivis envoient leur position`,
          },
          { label: 'En mouvement', valeur: enMouvement, unite: part(enMouvement) },
          {
            label: 'À l’arrêt',
            valeur: aLArret,
            unite: arretsLongs ? `dont ${arretsLongs} > 2 h` : part(aLArret),
            ton: arretsLongsSeuls ? 'attention' : 'neutre',
            aide: arretsLongsSeuls
              ? 'Cliquer pour revoir tous les camions'
              : 'Cliquer pour ne voir que les camions arrêtés depuis plus de 2 h',
            onClick: () => setArretsLongsSeuls((v) => !v),
            testid: 'stat-arret',
          },
          {
            label: 'Sans signal',
            valeur: horsLigne,
            ton: horsLigne ? 'attention' : 'neutre',
            unite: part(horsLigne),
            aide: 'Camions qui ne donnent plus de position depuis plus de 5 minutes',
          },
        ]}
        alertesSousTitre
        alertes={[
          trackingAlerts.length > 0 && {
            niveau: 'attention',
            court: 'Alertes GPS et zones',
            nombre: trackingAlerts.length,
            texte: trackingAlerts
              .slice(0, 4)
              .map(
                (event) =>
                  `${texteSur(event.title, libelle('alerteEvent', event.event_type, `Alerte ${LEXIQUE.suiviGps}`))} : ${texteSur(event.message, 'Position ou zone à vérifier.')}`,
              )
              .join(' — '),
            action: { label: 'Voir toutes les alertes', to: '/alerts' },
          },
        ]}
        meta={
          /* Le ratio « 12 / 30 en ligne » qui vivait ici disait déjà, en
             moins lisible, ce que dit la ligne de chiffres juste dessous. Ne
             reste que l'état du flux, qu'aucun compteur ne peut révéler: un
             compteur figé et un flux coupé se ressemblent à l'écran. */
          <>
            <span
              className={cn(
                'inline-flex items-center gap-1.5',
                connected ? 'text-success' : 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  connected ? 'animate-pulse bg-success' : 'border-2 border-muted-foreground',
                )}
                aria-hidden="true"
              />
              {connected ? 'Flux direct actif' : 'Flux direct interrompu'}
            </span>
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowList((v) => !v)}
              aria-pressed={showList}
              className="btn-secondary px-3"
            >
              <List className="h-4 w-4" aria-hidden="true" />
              {showList ? 'Masquer la liste' : 'Afficher la liste'}
            </button>
            <button onClick={refresh} className="btn-secondary px-3">
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Rafraîchir
            </button>
          </>
        }
      />

      {/* Customisation bar */}
      <div className="bg-card rounded-lg border shadow-sm p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Fond :</span>
          <div className="flex gap-1">
            {Object.entries(MAP_LAYERS).map(([key, l]) => (
              <button
                key={key}
                onClick={() => persistPrefs({ ...prefs, layer: key })}
                className={`text-xs px-3 py-1 rounded border transition ${
                  fondActif === key
                    ? 'bg-info text-info-foreground border-info'
                    : 'bg-card border-border hover:bg-muted/50'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {estSuperAdmin && (
          <div className="w-56 max-w-full">
            <SearchableSelect
              value={selectedOrg}
              onValueChange={setSelectedOrg}
              aria-label="Société"
              placeholder="Toutes les sociétés"
              searchPlaceholder="Rechercher une société…"
              emptyMessage="Aucune société trouvée"
              options={organizationOptions}
              className="rounded-lg bg-card text-xs"
            />
          </div>
        )}

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={arretsLongsSeuls}
            onChange={(e) => setArretsLongsSeuls(e.target.checked)}
          />
          Arrêtés depuis plus de 2 h
        </label>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={prefs.showTrails}
            onChange={(e) =>
              persistPrefs({ ...prefs, showTrails: e.target.checked })
            }
          />
          Afficher les traces
        </label>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={prefs.showAlerts !== false}
            onChange={(e) => persistPrefs({ ...prefs, showAlerts: e.target.checked })}
          />
          Alertes sur la carte
        </label>

        {lastRefresh && (
          <span className="text-xs text-muted-foreground/70 ml-auto">
            Dernière actualisation : {formatHeure(lastRefresh)}
          </span>
        )}
      </div>

      {/* Une carte satellite centrée sur le Sahel, sans un seul point
          dessus, ne dit pas à un nouveau client s'il a mal configuré quelque
          chose ou si le produit ne marche pas. Tant qu'aucun véhicule ne
          remonte, l'écran l'explique avant de montrer la carte. */}
      {!loading && vehicles.length === 0 && (
        <EmptyState
          icon={TruckIcon}
          title="Aucun camion ne remonte sa position"
          description="Cet écran suit vos camions en direct sur la carte : position, vitesse, chauffeur joignable en un clic. Un camion y apparaît dès que son boîtier GPS est rattaché, ou dès que son chauffeur roule avec l’application mobile ouverte."
          // Le point de blocage est le rattachement du boîtier, pas la carte:
          // le bouton mène là où le suivi se configure.
          actionLabel="Configurer le suivi GPS"
          onAction={() => navigate('/gps-providers')}
        />
      )}

      {/* Côte à côte, et non superposés.
          Le panneau flottait au-dessus de la carte: il masquait le tiers droit
          du Sahel, c'est-à-dire la moitié du corridor Bamako–Niamey, et
          l'exploitant devait le refermer pour voir ses propres camions. Deux
          colonnes règlent le conflit sans rien perdre: une sélection dans la
          liste se lit toujours immédiatement sur la carte, puisque les deux
          partagent `selectedTruckId`.
          En dessous de 1280 px la carte passe pleine largeur et la liste
          dessous — à cette largeur, deux colonnes ne laisseraient ni carte
          lisible ni liste utilisable. */}
      <div
        className={cn(
          'grid gap-4',
          showList && 'xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]',
        )}
      >
        {/* Sur téléphone la carte tient l'écran moins l'en-tête (100dvh : la
            barre d'adresse mobile ne la rogne pas) et la liste vient dessous. */}
        <MapCanvas height="h-[calc(100dvh-10rem)] min-h-[320px] xl:h-[calc(100vh-22rem)] xl:min-h-[420px]">
          {loading ? (
            <div className="flex h-full items-center justify-center text-muted-foreground/70">
              Chargement de la carte…
            </div>
          ) : (
            <FleetMap
              vehicles={filtered}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              now={maintenant}
              layer={fondActif}
              showTrails={prefs.showTrails}
              history={history}
              geoLocations={geoLocations}
              alertes={prefs.showAlerts === false ? undefined : alertesDeLaSociete}
            />
          )}
        </MapCanvas>

        {showList ? (
          <aside className="flex h-[32rem] flex-col overflow-hidden rounded-xl border border-border bg-card xl:h-[calc(100vh-22rem)] xl:min-h-[420px]">
            <div className="flex items-center gap-1 border-b border-border p-2" role="group" aria-label="Contenu du panneau">
              {[
                { cle: 'camions', label: `Camions (${filtered.length})` },
                { cle: 'zones', label: 'Lieux et zones' },
              ].map((onglet) => (
                <button
                  key={onglet.cle}
                  type="button"
                  onClick={() => setPanneau(onglet.cle)}
                  aria-pressed={panneau === onglet.cle}
                  className={cn(
                    'flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors',
                    panneau === onglet.cle
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {onglet.label}
                </button>
              ))}
            </div>
            {panneau === 'camions' ? (
              <FleetList
                variant="panel"
                vehicles={affiches}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                now={maintenant}
                onlineOnly={prefs.onlineOnly}
                onToggleOnlineOnly={(v) => persistPrefs({ ...prefs, onlineOnly: v })}
              />
            ) : (
              <ZonesList zones={geoLocations} />
            )}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
